/**
 * Hybrid Firestore Service
 * Uses API backend when available, falls back to direct Firestore access
 * This ensures the app works in staging environments without a backend API
 */

import { firestoreService as apiFirestoreService } from './firestoreService';
import * as directFirestore from './firestore';
import { UserProfile, WorkoutSession, WeeklyProgress } from '@/types/workout';
import { ApiError } from './apiClient';
import { auth } from '@/config/firebase';

class HybridFirestoreService {
  private useDirectFirestore = true; // Force direct Firestore for now to debug profile persistence
  private apiHealthChecked = true;

  /**
   * Check if we should use direct Firestore access
   */
  private async shouldUseDirectFirestore(): Promise<boolean> {
    // If we've already determined to use direct Firestore, continue using it
    if (this.useDirectFirestore) {
      return true;
    }

    // If we haven't checked API health yet, do it now
    if (!this.apiHealthChecked) {
      try {
        const isConnected = await apiFirestoreService.checkApiConnectivity();
        this.apiHealthChecked = true;
        
        if (!isConnected) {
          // API not connected, using direct Firestore
          this.useDirectFirestore = true;
          return true;
        }
        
        return false;
      } catch (error) {
        // API health check failed, using direct Firestore
        this.useDirectFirestore = true;
        this.apiHealthChecked = true;
        return true;
      }
    }

    return false;
  }

  /**
   * Execute operation with fallback
   */
  private async executeWithFallback<T>(
    apiOperation: () => Promise<T>,
    firestoreOperation: () => Promise<T>
  ): Promise<T> {
    const useDirect = await this.shouldUseDirectFirestore();
    
    if (useDirect) {
      return firestoreOperation();
    }

    try {
      return await apiOperation();
    } catch (error) {
      // If API fails, try direct Firestore as fallback
      if (error instanceof ApiError || (error as any)?.message?.includes('network')) {
        // API operation failed, falling back to direct Firestore
        this.useDirectFirestore = true;
        return firestoreOperation();
      }
      throw error;
    }
  }

  /**
   * User Profile Operations
   */
  async createUserProfile(userId: string, profile: Omit<UserProfile, 'uid'>): Promise<void> {
    return this.executeWithFallback(
      () => apiFirestoreService.createUserProfile(userId, profile),
      () => directFirestore.createUserProfile(userId, profile)
    );
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    // Getting user profile from hybrid service
    try {
      // Try direct Firestore first for profile retrieval to ensure reliability
      const profile = await directFirestore.getUserProfile(userId);

      return profile;
    } catch (error) {
      // Direct Firestore profile retrieval failed, trying API
      try {
        const profile = await apiFirestoreService.getUserProfile(userId);

        return profile;
      } catch (apiError) {
        // API profile retrieval also failed
        return null;
      }
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    return this.executeWithFallback(
      () => apiFirestoreService.updateUserProfile(userId, updates),
      () => directFirestore.updateUserProfile(userId, updates)
    );
  }

  /**
   * Workout Session Operations
   */
  async saveWorkoutSession(userId: string, session: WorkoutSession): Promise<void> {
    return this.executeWithFallback(
      () => apiFirestoreService.saveWorkoutSession(userId, session),
      async () => {
        await directFirestore.saveWorkoutSession(session);
        return;
      }
    );
  }

  async getWorkoutSession(sessionId: string): Promise<WorkoutSession | null> {
    return this.executeWithFallback(
      () => apiFirestoreService.getWorkoutSession(sessionId),
      () => directFirestore.getWorkoutSession('', sessionId) // userId not needed for direct access
    );
  }

  async getUserWorkoutSessions(userId: string, week?: number): Promise<WorkoutSession[]> {
    return this.executeWithFallback(
      () => apiFirestoreService.getUserWorkoutSessions(userId, week),
      () => directFirestore.getUserWorkoutSessions(userId, week)
    );
  }

  /**
   * Progress Operations
   */
  async saveWeeklyProgress(userId: string, progress: WeeklyProgress): Promise<void> {
    return this.executeWithFallback(
      () => apiFirestoreService.saveWeeklyProgress(userId, progress),
      async () => {
        // Direct Firestore doesn't have this function yet, so we'll skip it for now
        // Weekly progress save skipped - not implemented in direct Firestore
        return;
      }
    );
  }

  async getWeeklyProgress(userId: string, week: number): Promise<WeeklyProgress | null> {
    return this.executeWithFallback(
      () => apiFirestoreService.getWeeklyProgress(userId, week),
      async () => {
        // Direct Firestore doesn't have this function yet, so we'll return null for now
        // Weekly progress get not implemented in direct Firestore
        return null;
      }
    );
  }

  /**
   * Real-time subscriptions
   */
  subscribeToUserProfile(
    userId: string,
    callback: (_profile: UserProfile | null) => void
  ): () => void {
    // Subscribing to user profile changes

    // Always use direct Firestore for subscriptions to ensure reliability
    // API subscriptions can be unreliable and cause profile persistence issues

    this.useDirectFirestore = true;

    try {
      return directFirestore.subscribeToUserProfile(userId, callback);
    } catch (error) {
      // Failed to subscribe to user profile
      // Call callback with null to indicate failure
      callback(null);
      // Return a no-op unsubscribe function
      return () => {};
    }
  }

  /**
   * Utility methods
   */
  async checkApiConnectivity(): Promise<boolean> {
    if (this.useDirectFirestore) {
      return false;
    }
    return apiFirestoreService.checkApiConnectivity();
  }

  async getApiStatus(): Promise<{
    connected: boolean;
    latency?: number;
    error?: string;
    usingDirectFirestore: boolean;
  }> {
    const apiStatus = await apiFirestoreService.getApiStatus();
    return {
      ...apiStatus,
      usingDirectFirestore: this.useDirectFirestore,
    };
  }

  /**
   * Force direct Firestore mode (for testing or when API is known to be unavailable)
   */
  forceDirectFirestoreMode(): void {
    this.useDirectFirestore = true;
    this.apiHealthChecked = true;
  }

  /**
   * Reset to try API again
   */
  resetApiMode(): void {
    this.useDirectFirestore = false;
    this.apiHealthChecked = false;
  }

  /**
   * Debug method to check current service state
   */
  async debugServiceState(): Promise<{
    useDirectFirestore: boolean;
    apiHealthChecked: boolean;
    apiConnected: boolean;
    currentUser: string | null;
  }> {
    const apiConnected = await this.checkApiConnectivity();

    // Get current user
    const currentUser = auth.currentUser?.uid || null;

    return {
      useDirectFirestore: this.useDirectFirestore,
      apiHealthChecked: this.apiHealthChecked,
      apiConnected,
      currentUser,
    };
  }
}

// Create singleton instance
export const hybridFirestoreService = new HybridFirestoreService();

// Check environment variable to determine if we should enable fallback
const enableFallback = import.meta.env.VITE_ENABLE_FIRESTORE_FALLBACK === 'true';

if (enableFallback) {
  // Firestore fallback enabled via environment variable
}

// Export the service to use instead of the regular firestoreService
export default hybridFirestoreService;

// Global debug function for browser console
declare global {
   
  interface Window {
    debugHyroxProfilePersistence: () => Promise<void>;
    debugCreateTestProfile: () => Promise<void>;
  }
}

// Make debug function available globally
if (typeof window !== 'undefined') {
  window.debugHyroxProfilePersistence = async () => {
    // Debug Hyrox Profile Persistence
    try {
      // Check service state
      const serviceState = await hybridFirestoreService.debugServiceState();

      if (!serviceState.currentUser) {
        // No current user found
        return;
      }

      // Try to get profile using both methods
      // Testing profile retrieval methods...
      // Test API method
      try {
        const _apiProfile = await apiFirestoreService.getUserProfile(serviceState.currentUser);

        if (_apiProfile) {
          // API profile found
        }
      } catch (_error) {
        // API profile retrieval failed
      }

      // Test direct Firestore method
      try {
        const _directProfile = await directFirestore.getUserProfile(serviceState.currentUser);

        if (_directProfile) {
          // Direct Firestore profile found
        }
      } catch (_error) {
        // Direct Firestore profile retrieval failed
      }

      // Test hybrid method
      try {
        const _hybridProfile = await hybridFirestoreService.getUserProfile(serviceState.currentUser);

        if (_hybridProfile) {
          // Hybrid service profile found
        }
      } catch (_error) {
        // Hybrid service profile retrieval failed
      }

    } catch (_error) {
      // Debug profile persistence failed
    }
  };

  // Also add a simple profile creation test
  window.debugCreateTestProfile = async () => {
    // Creating Test Profile
    try {
      const user = auth.currentUser;

      if (!user) {
        // No authenticated user found
        return;
      }

      const testProfile = {
        email: user.email || 'test@example.com',
        name: 'Test User',
        sex: 'male' as const,
        currentMaxes: {
          backSquat: 225,
          deadlift: 275,
          benchPress: 185,
          overheadPress: 115,
          barbellRow: 155,
          frontSquat: 185,
        },
        competitionDate: undefined,
        startDate: new Date(),
        preferences: {
          units: 'lbs' as const,
          notifications: true,
          reminderTime: '08:00',
          theme: 'light' as const,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await hybridFirestoreService.createUserProfile(user.uid, testProfile);
      // Test profile created successfully
      // Try to retrieve it
      await hybridFirestoreService.getUserProfile(user.uid);
      // Retrieved test profile
    } catch (_error) {
      // Failed to create test profile
    }
  };
}
